<template>
    <div class="-m-6 bg-white">
        <div class="relative flex justify-center bg-gray-200 w-full" :class="{'h-128': header_image === null}">
            <template v-if="header_image !== null" class="relative">
                <figure v-if="header_image.mime_type.includes('image/')" class="aspect-w-16 aspect-h-9 w-full">
                    <img
                        class="object-cover"
                        :src="header_image.original_url"
                        :alt="header_image.name"
                    >
                </figure>
                <video
                    v-else-if="header_image.mime_type.includes('video/')"
                    playsinline
                    autoplay
                    muted
                    loop
                    class="w-full object-cover"
                    :src="header_image.original_url"
                    :type="header_image.mime_type"
                >
                    Your browser does not support the video tag.
                </video>
                <div v-else class="flex flex-col justify-center items-center pointer-events-none bg-indigo-100 text-indigo-700 object-cover group-hover:opacity-75">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <span>Unknown media type</span>
                </div>
                <div class="absolute top-0 bg-gradient-to-b w-screen from-gray-800 to-transparent h-1/2"></div>
                <div class="absolute bottom-0 w-screen mx-auto bg-gradient-to-b from-transparent to-black h-1/2 md:py-16"></div>
            </template>
            <div class="absolute p-10 inset-0">
                <button
                    type="button"
                    class="fixed z-20 top-20 right-0 h-14 w-14 bg-white border rounded-bl-lg md:rounded-tl-lg"
                    @click="openMenu"
                >
                    <div class="flex h-full flex-col justify-center items-center gap-y-2">
                        <svg
                            width="28"
                            height="2"
                            viewBox="0 0 28 2"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <line
                                x1="28"
                                y1="1"
                                x2="-8.74228e-08"
                                y2="0.999998"
                                stroke="#212121"
                                stroke-width="2"
                            />
                        </svg>
                        <svg
                            width="19"
                            height="2"
                            class="-ml-2"
                            viewBox="0 0 19 2"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <line
                                x1="19"
                                y1="1"
                                y2="1"
                                stroke="#212121"
                                stroke-width="2"
                            />
                        </svg>
                    </div>
                </button>
                <button
                    type="button"
                    class="calendarButton z-50 right-0 h-14 w-14 bg-white border rounded-bl-lg  md:rounded-tl-lg"
                    @click="showModal = true"
                >
                    <div class="flex h-full flex-col justify-center items-center">
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 6.00001C2 4.89544 2.89543 4.00002 3.99999 4.00002H18C19.1045 4.00002 20 4.89544 20 6.00001V17.9999C20 19.1045 19.1045 19.9999 18 19.9999H3.99999C2.89543 19.9999 2 19.1045 2 17.9999V6.00001Z" fill="white"/>
                            <path d="M6.99999 6.00001V2.00003M15 6.00001V2.00003M5.99999 9.99999H16M3.99999 19.9999H18C19.1045 19.9999 20 19.1045 20 17.9999V6.00001C20 4.89544 19.1045 4.00002 18 4.00002H3.99999C2.89543 4.00002 2 4.89544 2 6.00001V17.9999C2 19.1045 2.89543 19.9999 3.99999 19.9999Z" stroke="#374151" stroke-width="2.39999" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </div>
                </button>
                <media-select-single
                    :media="media"
                    :selected="header_image ?? proposal.image"
                    @update="updateMedia"
                    :proposal="proposal"
                />
            </div>
            <div class="absolute bottom-20 left-20 text-white">
                <h1 class="text-5xl font-bold">{{ proposal.title }}</h1>
                <h2 class="text-xl">{{ proposal.description }}</h2>
            </div>
        </div>
        <div class="relative bg-white rounded border border-1 mx-5 lg:mx-20  -mt-10 z-10">
            <FeaturedBulletsEditable
                :client="proposal.client.name"
                :start_date="proposal.start_date"
                :end_date="proposal.end_date"
                :destination="proposal.places[0].name"
            />
        </div>
        <TripIntroEditable
            :proposal="proposal"
            :media="media"
            @updateGalleryImages="updateGalleryImages"
            :gallery_images="gallery_images"
            :loading_gallery_images="loading_gallery_images"
            :header_image="header_image"
            @editMedia="editMedia"
        />

        <TripHighlighstEditable
            :cards="cards"
            :proposal="proposal"
        />

        <DestinationIntroEditable
            :proposal="proposal"
        />

        <DestinationActivitiesEditable
            @activityCard="activityCard"
        />

        <DestinationProgramEditable
            :proposal="proposal"
            :media="media"
            @updateGalleryImages="updateGalleryImages"
            :gallery_images="gallery_images"
            :loading_gallery_images="loading_gallery_images"
            @editMedia="editMedia"
        />

        <DestinationAccommodationEditable
            :proposal="proposal"
        />

        <DestinationEateryEditable
            :proposal="proposal"
        />

        <DestinationPlanEditable
            :proposal="proposal"
        />

        <DestinationGalleryEditable
            :proposal="proposal"
            :media="media"
            @updateGalleryImages="updateGalleryImages"
            :gallery_images="gallery_images"
            :loading_gallery_images="loading_gallery_images"
            @editMedia="editMedia"
        />

        <ProgramEditable
            :proposal="proposal"
        />

        <FlightsEditable
            :proposal="proposal"
        />

        <PriceOfferEditable
            :proposal="proposal"
        />
    </div>
    <media-editor
        :media="edit_file"
        @close="edit_file = {}"
    />
    <Modal
        :show="showModal"
        title="Calendar"
        @close="showModal = false"
        :size="`max-w-6xl`"
    >
        <Calendar
            :start="proposal.start_date"
            :end="proposal.end_date"
            :title="proposal.title"
        />
        <div class="w-full flex justify-end pb-4 pr-2">
            <button @click="showModal = false" type="button" class="bg-success py-1 px-2 rounded-lg text-gray-100 mr-4">Close</button>
        </div>
    </Modal>
</template>

<script setup>

import {computed, onMounted, ref} from "vue";
import MediaSelectSingle from "@/Components/Media/MediaPickerSingle.vue";
import MediaEditor from "@/ComponentsDeprecated/MediaEditor";
import FeaturedBulletsEditable from "@/ComponentsDeprecated/Proposal/FeaturedBulletsEditable";
import Calendar from "@/ComponentsDeprecated/Calendar/Calendar";
import Modal from '@/ComponentsDeprecated/Modal/CalendarModal'
import Button from "@/Jetstream/Button";
import PriceOfferEditable from "@/ComponentsDeprecated/Proposal/PriceOfferEditable";
import FlightsEditable from "@/ComponentsDeprecated/Proposal/FlightsEditable";
import ProgramEditable from "@/ComponentsDeprecated/Proposal/ProgramEditable";
import DestinationGalleryEditable from "@/ComponentsDeprecated/Proposal/DestinationGalleryEditable";
import DestinationPlanEditable from "@/ComponentsDeprecated/Proposal/DestinationPlanEditable";
import DestinationEateryEditable from "@/ComponentsDeprecated/Proposal/DestinationEateryEditable";
import DestinationAccommodationEditable from "@/ComponentsDeprecated/Proposal/DestinationAccommodationEditable";
import DestinationProgramEditable from "@/ComponentsDeprecated/Proposal/DestinationProgramEditable";
import DestinationActivitiesEditable from "@/ComponentsDeprecated/Proposal/DestinationActivitiesEditable";
import DestinationIntroEditable from "@/ComponentsDeprecated/Proposal/DestinationIntroEditable";
import TripHighlighstEditable from "@/ComponentsDeprecated/Proposal/TripHighlightsEditable";
import TripIntroEditable from "@/ComponentsDeprecated/Proposal/TripIntroEditable";

const props = defineProps({
    proposal: {
        type: Object,
        required: true
    },
    media: {
        type: Array,
        default: []
    }
})

const gallery_images = ref([])
const header_image = ref(null)
const loading_gallery_images = ref(false)
const edit_file = ref({})
const showModal = ref(false)
const editable = ref(false)
let cards = ref([])

const places_string = computed(() => {
    let places = [];
    props.proposal.places.forEach((place) => {
        places.push(place.name + ' - ' + place.area)
    })
    return places.join(', ')
})

onMounted(() => {
    gallery_images.value = props.proposal.gallery
    header_image.value = props.proposal.image
})

function makeEditable() {
    editable.value = true
}

function notEditable() {
    editable.value = false
}

function updateGalleryImages(imgs) {
    loading_gallery_images.value = true
    axios.post(route('api.proposal.gallery.sync', {proposal: props.proposal.id}), {
        images: imgs
    }).then(response => {
        gallery_images.value = response.data.images
    }).catch(error => {
        console.log('error')
        console.log(error)
    }).finally(() => {
        loading_gallery_images.value = false
    })
}

function updateMedia(media) {
    axios.post(route('api.proposal.store', {proposal: props.proposal.id}), {
        media_id: media.id
    }).then(response => {
        header_image.value = media
    }).catch(error => {
        console.log('error')
        console.log(error)
    }).finally(() => {

    })
}

function editMedia(file) {
    edit_file.value = file
}

function activityCard(activityCard) {
    let card =  {
        id: activityCard.cards.id,
        title: activityCard.cards.headline,
        date: activityCard.cards.start,
        destination: activityCard.cards.location,
        img: activityCard.cards.img,
        alt: activityCard.cards.alt
    }
    cards.value.push(card)
}

function openMenu() {
    // document.getElementById('burgerMenu').classList.replace('-right-48', 'right-0');
}


</script>

<style scoped>


</style>
